import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './styles.css';

/**
 * SearchableSelect - A searchable dropdown for selecting missions
 * Uses a portal to render the dropdown outside the modal's overflow:hidden container
 */
const SearchableSelect = ({ value, options, onChange, placeholder = "Choose a mission...", allowClear = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find the selected option to display its name
  const selectedOption = options.find(opt => opt.id === value);
  
  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => 
      opt.name?.toLowerCase().includes(term)
    );
  }, [options, searchTerm]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedEl = listRef.current.children[highlightedIndex];
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Update dropdown position when open
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    
    const updatePosition = () => {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  const handleSelect = (optionId) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  const dropdownContent = isOpen && createPortal(
    <div 
      ref={dropdownRef}
      className="searchable-select__dropdown"
      style={{
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
    >
      {filteredOptions.length === 0 ? (
        <div className="searchable-select__no-results">
          No missions found
        </div>
      ) : (
        <ul className="searchable-select__list" ref={listRef}>
          {allowClear && (
            <li
              className={`searchable-select__option searchable-select__option--placeholder ${!value ? 'searchable-select__option--selected' : ''} ${highlightedIndex === -1 ? 'searchable-select__option--highlighted' : ''}`}
              onClick={() => handleSelect('')}
            >
              {placeholder}
            </li>
          )}
          {filteredOptions.map((opt, index) => (
            <li
              key={opt.id}
              className={`searchable-select__option ${opt.id === value ? 'searchable-select__option--selected' : ''} ${index === highlightedIndex ? 'searchable-select__option--highlighted' : ''}`}
              onClick={() => handleSelect(opt.id)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {opt.nodeType === 'decision' && <span className="searchable-select__option-icon">🔷</span>}
              <span className="searchable-select__option-text">{opt.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body
  );

  return (
    <div className="searchable-select" ref={containerRef}>
      <div 
        className={`searchable-select__trigger ${isOpen ? 'searchable-select__trigger--open' : ''}`}
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          type="text"
          className="searchable-select__input"
          value={isOpen ? searchTerm : (selectedOption?.name || '')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={selectedOption ? '' : placeholder}
        />
        {!isOpen && selectedOption?.nodeType === 'decision' && (
          <span className="searchable-select__decision-icon">🔷</span>
        )}
        <svg className="searchable-select__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {dropdownContent}
    </div>
  );
};

export default SearchableSelect;
